import {
  AiFormDiffRowState,
  AiFormDiffUnresolvedReason,
  AiFormStatus,
  AiFormTransitionAction,
} from 'constants/aiProcessRuns';
import { StatusEnum } from 'constants/status';

import { FIELD_TRIAGE_TONE } from './formReview.utils';

export const FormFillState = {
  FILLING: 'filling',
  PAUSED: 'paused',
  DONE: 'done',
};

export const FORM_REVIEW_LABELS = {
  CARD_TITLE: 'Form review',
  OPEN_SURFACE: 'Review form',
  SURFACE_TITLE: 'Form review',
  SURFACE_CLOSE: 'Close',
  REVIEWED_BY: 'Reviewed by',
  LIVE_FILL: 'AI is still filling this form…',
  PAUSED: 'Paused until the step waiting for your input is answered',
  AWAITING_REVIEW: 'Filling complete — review and mark ready',
  ALL_CLEAR: 'No fields need attention',
  NEEDS_REVIEW_CHIP: 'Needs review',
  SCHEMA_MISMATCH: 'This form was captured against an older schema version.',
  AGREE: 'Agree',
  OVERRIDE: 'Override',
  FLAG: 'Flag',
  OVERRIDE_PLACEHOLDER: 'Enter the corrected value',
  OVERRIDE_SAVE: 'Save override',
  OVERRIDE_CANCEL: 'Cancel',
  OVERRIDE_VALUE_LABEL: 'Corrected value',
  CONFIDENCE: 'Confidence',
  PROVENANCE: 'Source',
  NO_VALUE: '—',
  EMPTY_SECTION: 'No fields in this section.',
  BOOLEAN_TRUE: 'Yes',
  BOOLEAN_FALSE: 'No',
  UNMAPPED_SECTION_TITLE: 'Unmapped fields',
  UNMAPPED_FIELD_NOTE: 'Not in the current schema',
  CHANGED_AFTER_REVIEW: 'Changed since you reviewed',
  CHANGED_WAS: 'was',
  CHANGED_NOW: 'now',
  SIGN_OFF_TITLE: 'Confirm before you approve',
  SIGN_OFF_AGREED: 'Agreed',
  SIGN_OFF_OVERRIDDEN: 'Overridden',
  SIGN_OFF_FLAGGED: 'Flagged',
  SIGN_OFF_PENDING: 'Still pending',
  SIGN_OFF_BLOCKING:
    'Some fields are still pending or flagged. Approving now may be rejected on the server.',
  SIGN_OFF_SCHEMA_DRIFT: 'This form was captured against an older schema — approve field by field.',
  SIGN_OFF_CHANGED: 'Changed after review',
  SIGN_OFF_CONFIRM: 'Confirm approve',
  SIGN_OFF_CANCEL: 'Back',
  UNVERIFIED_NOTE: 'Unverified — could not be matched to a loan income record',
  DIFF_AI_HEADING: 'AI',
  DIFF_RECORD_HEADING: 'Loan record',
  DIFF_VARIANCE_HEADING: 'Monthly variance',
  DIFF_STATE_HEADING: 'Status',
  BULK_AGREE: 'Agree all low-risk',
  BULK_TITLE: 'Agree all low-risk fields',
  BULK_CONFIRM: 'Agree all',
  BULK_CANCEL: 'Cancel',
  BULK_NONE_ELIGIBLE: 'No fields are eligible for one-click agreement.',
  BULK_EXCLUDED_TITLE: 'Held back for individual review',
};

export const BULK_EXCLUSION_LABELS = {
  flagged: 'Flagged',
  unverified: 'Unverified',
  highVariance: 'Material variance',
  nullConfidence: 'No confidence score',
  schemaDrift: 'Schema drifted',
};

export const formatBulkAgreeCount = count =>
  count === 1 ? 'Agree 1 low-risk field' : `Agree ${count} low-risk fields`;

export const DIFF_STATE_LABELS = {
  [AiFormDiffRowState.MATCHED]: 'Matched',
  [AiFormDiffRowState.AI_ONLY]: 'Found in docs, not on loan',
  [AiFormDiffRowState.RECORD_ONLY]: 'On loan, not addressed by AI',
};

export const DIFF_UNRESOLVED_REASON_LABELS = {
  [AiFormDiffUnresolvedReason.NO_RECORD_MATCH]: 'No matching loan income',
  [AiFormDiffUnresolvedReason.BORROWER_DELETED]: 'Borrower removed from loan',
  [AiFormDiffUnresolvedReason.AMBIGUOUS_MATCH]: 'Matched more than one income',
};

export const formatNeedsAttention = count =>
  count === 1 ? '1 field needs attention' : `${count} fields need attention`;

export const formatFieldProgress = (filled, total) => `${filled} of ${total} fields filled`;

export const DRAFTING_STATUS_LABELS = {
  [FormFillState.FILLING]: 'Drafting',
  [FormFillState.PAUSED]: 'Paused',
  [FormFillState.DONE]: 'Draft',
};

export const FORM_STATUS_LABELS = {
  [AiFormStatus.DRAFTING]: 'Drafting',
  [AiFormStatus.READY_FOR_REVIEW]: 'Ready for review',
  [AiFormStatus.VALIDATED]: 'Validated',
  [AiFormStatus.APPROVED]: 'Approved',
  [AiFormStatus.NEEDS_REWORK]: 'Needs rework',
};

export const FORM_TRANSITION_TONE = {
  AFFIRMATIVE: 'affirmative',
  SECONDARY: 'secondary',
};

export const FORM_TRANSITION_DEFINITIONS = {
  READY_FOR_REVIEW: {
    action: AiFormTransitionAction.READY_FOR_REVIEW,
    label: 'Mark ready for review',
    tone: FORM_TRANSITION_TONE.AFFIRMATIVE,
  },
  VALIDATE: {
    action: AiFormTransitionAction.VALIDATE,
    label: 'Validate',
    tone: FORM_TRANSITION_TONE.SECONDARY,
  },
  APPROVE: {
    action: AiFormTransitionAction.APPROVE,
    label: 'Approve',
    tone: FORM_TRANSITION_TONE.AFFIRMATIVE,
  },
  SEND_BACK_FOR_REWORK: {
    action: AiFormTransitionAction.SEND_BACK_FOR_REWORK,
    label: 'Send back',
    tone: FORM_TRANSITION_TONE.SECONDARY,
  },
};

export const TRIAGE_TONE_TO_STATUS = {
  [FIELD_TRIAGE_TONE.GREEN]: StatusEnum.SUCCESS,
  [FIELD_TRIAGE_TONE.AMBER]: StatusEnum.WARNING,
  [FIELD_TRIAGE_TONE.RED]: StatusEnum.ERROR,
};

export const FormReviewTestIds = {
  CARD: 'AiActivity_FormReviewCard_Card',
  OPEN_SURFACE_BUTTON: 'AiActivity_FormReviewCard_OpenSurfaceButton',
  SURFACE: 'AiActivity_FormReviewSurface_Surface',
  SURFACE_CLOSE_BUTTON: 'AiActivity_FormReviewSurface_CloseButton',
  SURFACE_SECTION: 'AiActivity_FormReviewSurface_Section',
  SURFACE_FIELD_ROW: 'AiActivity_FormReviewSurface_FieldRow',
  OVERRIDE_BOOLEAN: 'AiActivity_FormReviewSurface_OverrideBoolean',
  OVERRIDE_ENUM: 'AiActivity_FormReviewSurface_OverrideEnum',
  OVERRIDE_DATE: 'AiActivity_FormReviewSurface_OverrideDate',
  OVERRIDE_NUMBER: 'AiActivity_FormReviewSurface_OverrideNumber',
  STATUS_PILL: 'AiActivity_FormReviewCard_StatusPill',
  SCHEMA_MISMATCH: 'AiActivity_FormReviewCard_SchemaMismatch',
  REVIEWED_BY: 'AiActivity_FormReviewCard_ReviewedBy',
  NEEDS_ATTENTION: 'AiActivity_FormReviewCard_NeedsAttention',
  PROGRESS_STRIP: 'AiActivity_FormReviewCard_ProgressStrip',
  LIVE_FILL: 'AiActivity_FormReviewCard_LiveFill',
  AWAITING_REVIEW: 'AiActivity_FormReviewCard_AwaitingReview',
  PAUSED: 'AiActivity_FormReviewCard_Paused',
  TRIAGE_DOT: 'AiActivity_FormReviewCard_TriageDot',
  TRANSITION_BUTTON: 'AiActivity_FormReviewCard_TransitionButton',
  FIELD_CELL: 'AiActivity_FormReviewCard_FieldCell',
  FIELD_VALUE: 'AiActivity_FormReviewCard_FieldValue',
  FIELD_TABLE: 'AiActivity_FormReviewCard_FieldTable',
  FIELD_TRIAGE_ICON: 'AiActivity_FormReviewCard_TriageIcon',
  PROVENANCE_BADGE: 'AiActivity_FormReviewCard_ProvenanceBadge',
  AGREE_BUTTON: 'AiActivity_FormReviewCard_AgreeButton',
  FLAG_BUTTON: 'AiActivity_FormReviewCard_FlagButton',
  OVERRIDE_BUTTON: 'AiActivity_FormReviewCard_OverrideButton',
  OVERRIDE_INPUT: 'AiActivity_FormReviewCard_OverrideInput',
  OVERRIDE_SAVE: 'AiActivity_FormReviewCard_OverrideSave',
  OVERRIDE_CANCEL: 'AiActivity_FormReviewCard_OverrideCancel',
  UNMAPPED_SECTION: 'AiActivity_FormReviewSurface_UnmappedSection',
  UNMAPPED_FIELD_ROW: 'AiActivity_FormReviewSurface_UnmappedFieldRow',
  CHANGED_AFTER_REVIEW: 'AiActivity_FormReviewSurface_ChangedAfterReview',
  SIGN_OFF_SUMMARY: 'AiActivity_FormReviewSurface_SignOffSummary',
  SIGN_OFF_BLOCKING: 'AiActivity_FormReviewSurface_SignOffBlocking',
  SIGN_OFF_SCHEMA_DRIFT: 'AiActivity_FormReviewSurface_SignOffSchemaDrift',
  SIGN_OFF_CONFIRM: 'AiActivity_FormReviewSurface_SignOffConfirm',
  SIGN_OFF_CANCEL: 'AiActivity_FormReviewSurface_SignOffCancel',
  UNVERIFIED_BADGE: 'AiActivity_FormReviewSurface_UnverifiedBadge',
  DIFF_TABLE: 'AiActivity_FormReviewSurface_DiffTable',
  DIFF_ROW: 'AiActivity_FormReviewSurface_DiffRow',
  BULK_AGREE_BUTTON: 'AiActivity_FormReviewSurface_BulkAgreeButton',
  BULK_SUMMARY: 'AiActivity_FormReviewSurface_BulkSummary',
  BULK_CONFIRM: 'AiActivity_FormReviewSurface_BulkConfirm',
  BULK_CANCEL: 'AiActivity_FormReviewSurface_BulkCancel',
  BULK_EXCLUDED_ROW: 'AiActivity_FormReviewSurface_BulkExcludedRow',
};
